// Per-trip & all-trips "proof of trip" exports (CSV + branded landscape PDF) for the
// Vehicle Driver Log Book.
import { jsPDF } from 'jspdf';
import { formatDT, formatTimeOnly, formatDuration, formatDistance, getFileTimestamp } from './timezone';

const EXPORT_LOGO = '/ENSUE_LIVETRAK.webp';

// Brand palette (matches the Analytics export styling). Exported so the Person
// Log Book export util can reuse the exact same look & feel.
export const SLATE_900 = [30, 41, 59];
export const SLATE_600 = [71, 85, 105];
export const SLATE_500 = [100, 116, 139];
export const BLUE = [37, 99, 235];
export const GREEN = [22, 163, 74];
const HEAD_BG = [241, 245, 249];
const ROW_BG = [248, 250, 252];
export const BORDER = [226, 232, 240];
const AMBER = [180, 83, 9];

export const roleLabel = (r) => {
  const k = String(r || '').toLowerCase();
  return k ? k.charAt(0).toUpperCase() + k.slice(1) : 'Occupant';
};

export const roleTransitionLabel = (transition) => {
  if (!transition?.changed_at || transition.reason !== 'rfid_driver_takeover') return '';
  const replacement = transition.replacement_credential || transition.replacement_person_name || '';
  return `Driver until ${formatTimeOnly(transition.changed_at, { seconds: true })} · changed to Staff when RFID Driver${replacement ? ` ${replacement}` : ''} took over`;
};

export const formatExportDateTime = (value) => value ? formatDT(value, { seconds: true }) : '-';

const decisionLabel = (audit = {}) => {
  const source = audit.decision_source;
  const reason = audit.resolution_reason;
  if (audit.selected_from_people_directory || source === 'manager_directory_override') return 'Manager assignment - BLE not detected';
  if (audit.overrode_declined_response) return 'Manager override of declined response';
  if (source === 'manager_override') return 'Manager override';
  if (source === 'driver_confirmation' && reason === 'last_remaining_after_rejections') return 'Auto-assigned - last remaining after others declined';
  if (source === 'driver_confirmation' && reason === 'accepted') return 'Driver ballot - accepted';
  if (source === 'ble_algorithm') return 'Auto-confirmed by BLE proximity';
  if (source === 'ibutton_rfid') return 'RFID / iButton scan';
  if (source === 'manual_assignment') return 'Assigned driver (manual)';
  return reason || source || '';
};

const responseLabel = (response) => response === 'rejected' ? 'Declined' : response === 'accepted' ? 'Accepted' : 'No response';

export const assignmentAuditRows = (audit = {}) => {
  if (!audit || !Object.keys(audit).length) return [];
  const rows = [];
  const decision = decisionLabel(audit);
  if (decision) rows.push(['Decision', decision]);
  if (audit.decided_at) rows.push(['Decision time', formatExportDateTime(audit.decided_at)]);
  const reason = audit.override_reason || audit.directory_assignment_reason
    || (audit.manager_confirmed_no_driver ? audit.resolution_reason : '');
  if (reason) rows.push(['Manager reason', reason]);
  if (audit.corrected_auto_assignment) {
    rows.push(['Correction', `Replaced automatic assignment${audit.previous_driver_person_name ? ` for ${audit.previous_driver_person_name}` : ''}`]);
  }
  if ((audit.candidate_responses || []).length) {
    rows.push(['Candidate responses', audit.candidate_responses.map((candidate) => {
      const at = candidate.responded_at ? ` at ${formatExportDateTime(candidate.responded_at)}` : '';
      return `${candidate.person_name || 'Unknown'}: ${responseLabel(candidate.response)}${at}`;
    }).join('; ')]);
  }
  return rows;
};

const ballotAuditRows = (trip) => {
  const ballots = trip.driver_ballots || [trip.pending_driver_ballot || trip.driver_ballot_resolution].filter(Boolean);
  return ballots.flatMap((ballot, index) => {
    const resolution = ballot.resolution || {};
    const audit = {
      ...resolution,
      decision_source: resolution.decision_source,
      resolution_reason: resolution.reason,
      candidate_responses: ballot.candidates || [],
      previous_driver_person_name: (ballot.resolution_history || []).find((row) => row.driver_person_id === resolution.previous_driver_person_id)?.driver_person_name,
    };
    const status = ballot.manager_correction_available ? 'Auto-assigned - manager correction available'
      : ballot.manager_review_required ? 'No Driver - manager review required'
      : ballot.status === 'pending' ? 'Pending'
      : resolution.manager_confirmed_no_driver ? 'No Driver - manager confirmed'
      : ballot.status || 'Resolved';
    return [
      [`Driver ballot${ballots.length > 1 ? ` #${index + 1}` : ''}`, status],
      ...(ballot.ballot_sent_at ? [['Ballot sent', formatExportDateTime(ballot.ballot_sent_at)]] : []),
      ...assignmentAuditRows(audit),
    ];
  });
};

// GPS location helpers (shared with the log book UI)
export const fmtCoords = (loc) => {
  if (!loc || loc.latitude == null || loc.longitude == null) return '';
  const lat = Number(loc.latitude), lng = Number(loc.longitude);
  if (Number.isNaN(lat) || Number.isNaN(lng)) return '';
  return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
};

export const tripLocMapsUrl = (loc) => {
  if (!loc || loc.latitude == null || loc.longitude == null) return null;
  const lat = Number(loc.latitude), lng = Number(loc.longitude);
  if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
  return `https://www.google.com/maps?q=${lat},${lng}`;
};

// A CSV cell that is a CLICKABLE Google-Maps link in Excel & Google Sheets.
// Uses the =HYPERLINK() formula (a plain URL string is not clickable in Excel).
export const csvMapsLink = (loc) => {
  const url = tripLocMapsUrl(loc);
  const coords = fmtCoords(loc);
  if (!url) return coords || '-';
  return `=HYPERLINK("${url}","${coords}")`;
};

export const safeName = (s) => String(s || '').replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '');
// Filename timestamp in the user's configured timezone (Settings > Date & Time),
// NOT UTC. Uses the shared timezone.js resolver so it matches on-screen times.
const stamp = () => getFileTimestamp();

export const downloadBlob = (content, filename, type) => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 100);
};

export const csvCell = (c) => {
  const s = String(c == null ? '' : c);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

// Load the brand logo -> PNG dataURL (canvas-converted, resolution-capped, CORS-safe).
export const loadLogoPng = async () => {
  try {
    const res = await fetch(EXPORT_LOGO);
    if (!res.ok) return null;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const img = await new Promise((resolve, reject) => {
      const im = new Image();
      im.onload = () => resolve(im);
      im.onerror = reject;
      im.src = url;
    });
    const maxH = 120;
    const scale = img.naturalHeight > maxH ? maxH / img.naturalHeight : 1;
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round((img.naturalWidth || 1) * scale));
    canvas.height = Math.max(1, Math.round((img.naturalHeight || 1) * scale));
    canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL('image/png');
    URL.revokeObjectURL(url);
    return { dataUrl, w: canvas.width, h: canvas.height };
  } catch {
    return null;
  }
};

// ----------------------------- CSV -----------------------------

const tripCsvRows = (trip, idx) => {
  const rows = [];
  rows.push([`Trip #${idx + 1}`, trip.is_active ? 'Ongoing' : 'Completed']);
  rows.push(['Started', formatExportDateTime(trip.started_at)]);
  rows.push(['Ended', trip.is_active ? 'In progress' : formatExportDateTime(trip.ended_at)]);
  rows.push(['Duration', trip.duration_seconds != null ? formatDuration(trip.duration_seconds) : '-']);
  rows.push(['Distance', trip.distance_km != null ? formatDistance(trip.distance_km, { decimals: 1 }) : '-']);
  rows.push(['Start location', csvMapsLink(trip.start_location)]);
  rows.push(['End location', csvMapsLink(trip.end_location)]);
  if (trip.qualification?.label) rows.push(['Qualification', trip.qualification.label + (trip.short_trip ? ' (Short trip)' : '')]);
  rows.push(...ballotAuditRows(trip));
  rows.push([]);
  rows.push(['Role', 'Name', 'Source', 'Credential', 'Boarded', 'Left']);
  if (!(trip.occupants || []).length && trip.no_driver) {
    rows.push(['-', 'No driver identified', '', '', '', '']);
  }
  (trip.occupants || []).forEach((o) => {
    rows.push([
      roleLabel(o.role || o.person_type),
      o.person_name || o.resolved_person_name || 'Unmatched',
      o.source_label || '',
      o.credential_identifier || '',
      formatExportDateTime(o.started_at),
      o.is_active ? 'Still onboard' : formatExportDateTime(o.ended_at),
    ]);
    const transition = roleTransitionLabel(o.role_transition);
    if (transition) rows.push(['Role history', transition]);
    if (o.allocation_note) rows.push(['Allocation / decision', o.allocation_note]);
    rows.push(...assignmentAuditRows(o.assignment_audit));
  });
  if ((trip.rejected_scans || []).length) {
    rows.push([]);
    rows.push([`Rejected scans (${trip.rejected_scans.length})`]);
    rows.push(['Name', 'Credential', 'Time']);
    trip.rejected_scans.forEach((r) => rows.push([r.person_name || 'Unknown', r.credential_identifier || '', formatExportDateTime(r.timestamp)]));
  }
  return rows;
};

export const rowsToCsv = (rows) => rows.map((r) => r.map(csvCell).join(',')).join('\n');

/* jsPDF standard fonts are WinAnsi-only: chars like '→' measure wrong in
   getTextWidth AND render as multi-glyph garbage, defeating cell truncation
   and bleeding into the next column. Map common symbols, drop the rest. */
const PDF_CHAR_MAP = { '\u2192': '->', '\u2190': '<-', '\u2018': "'", '\u2019': "'", '\u201c': '"', '\u201d': '"', '\u2022': '*' };
const sanitizePdfText = (s) => String(s ?? '')
  .replace(/[\u2192\u2190\u2018\u2019\u201c\u201d\u2022]/g, (c) => PDF_CHAR_MAP[c])
  .replace(/[^\x20-\x7E\u00A0-\u00FF\u2013\u2014\u2026]/g, '?');

/* Generic branded PDF table — paged, zebra rows, truncated cells.
   columns: [{ label, weight }], rows: array of string arrays. Returns final y. */
export const drawPdfTable = (doc, layout, startY, columns, rows) => {
  const { M, PAGE_H, CONTENT_W } = layout;
  const totalW = columns.reduce((a, c) => a + c.weight, 0);
  const widths = columns.map((c) => (c.weight / totalW) * CONTENT_W);
  const ROW_H = 14;
  let y = startY;
  const drawHeader = () => {
    doc.setFillColor(241, 245, 249);
    doc.rect(M, y, CONTENT_W, ROW_H, 'F');
    doc.setFont(undefined, 'bold'); doc.setFontSize(7.5); doc.setTextColor(...SLATE_600);
    let x = M;
    columns.forEach((c, i) => { doc.text(String(c.label).toUpperCase(), x + 4, y + 9); x += widths[i]; });
    y += ROW_H;
    doc.setFont(undefined, 'normal'); doc.setFontSize(8); doc.setTextColor(...SLATE_900);
  };
  drawHeader();
  rows.forEach((r, ri) => {
    if (y + ROW_H > PAGE_H - 44) { doc.addPage(); y = 44; drawHeader(); }
    if (ri % 2 === 1) { doc.setFillColor(248, 250, 252); doc.rect(M, y, CONTENT_W, ROW_H, 'F'); }
    let x = M;
    r.forEach((cell, i) => {
      const maxW = widths[i] - 8;
      let txt = sanitizePdfText(cell);
      while (doc.getTextWidth(txt) > maxW && txt.length > 1) txt = `${txt.slice(0, -2)}…`;
      doc.text(txt, x + 4, y + 9);
      x += widths[i];
    });
    doc.setDrawColor(...BORDER); doc.setLineWidth(0.3);
    doc.line(M, y + ROW_H, M + CONTENT_W, y + ROW_H);
    y += ROW_H;
  });
  return y;
};

/* Branded standalone HTML export (same look as Analytics History Exports) —
   embeds the logo as base64 so the file has zero external dependencies. */
export const exportTableHtml = async (title, subtitle, headers, rows, filename) => {
  let logoSrc = '';
  try {
    const res = await fetch(EXPORT_LOGO);
    if (res.ok) {
      const blob = await res.blob();
      logoSrc = await new Promise((resolve) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = () => resolve('');
        r.readAsDataURL(blob);
      });
    }
  } catch { /* logo optional */ }
  const escHtml = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const table = `<table><thead><tr>${headers.map((h) => `<th>${escHtml(h)}</th>`).join('')}</tr></thead><tbody>${rows.map((r) => `<tr>${r.map((c) => `<td>${escHtml(c)}</td>`).join('')}</tr>`).join('')}</tbody></table>`;
  const ts = new Date().toLocaleString();
  const html = `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"><title>${escHtml(title)}</title>
<style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:system-ui,-apple-system,sans-serif;background:#fff;color:#1e293b}
.header{height:60px;background:#fff;border-bottom:1px solid #e2e8f0;display:flex;align-items:center;padding:0 16px;gap:16px}
.header img{height:36px}.header-info{font-size:13px;color:#475569}.header-info b{color:#1e293b}
.content{padding:20px}table{width:100%;border-collapse:collapse;font-size:11px;margin-top:12px}
th{background:#f1f5f9;color:#475569;font-weight:600;text-align:left;padding:6px 8px;border:1px solid #e2e8f0;white-space:nowrap}
td{padding:5px 8px;border:1px solid #e2e8f0;vertical-align:top}tr:nth-child(even){background:#f8fafc}
.footer{margin-top:20px;padding-top:12px;border-top:1px solid #e2e8f0;font-size:10px;color:#475569;text-align:center}
@media print{.header{position:fixed;top:0;left:0;right:0;border-bottom:2px solid #2563eb}.content{margin-top:70px}@page{margin:10mm 8mm}}</style></head><body>
<div class="header">${logoSrc ? `<img src="${logoSrc}" alt="EnsueLIVETRAK"/>` : ''}<div class="header-info"><b>${escHtml(title)}</b>${subtitle ? ` &mdash; ${escHtml(subtitle)}` : ''}</div></div>
<div class="content">${table}<div class="footer">Generated by EnsueLIVETRAK &mdash; ${ts}</div></div></body></html>`;
  const blob = new Blob([html], { type: 'text/html;charset=utf-8;' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
};

// ---- Multi-section dashboard export (Person & Vehicle Dashboards) — PDF + HTML ----
// sections: [{ heading, columns: [{label, weight}], rows: [[...]] }] — empty sections skipped.
export const exportDashboardPdf = async (title, subtitle, sections, filename) => {
  const logo = await loadLogoPng();
  const { doc, layout } = newLandscapeDoc();
  let y = addBrandHeader(doc, layout, logo, title, subtitle);
  (sections || []).forEach((s) => {
    if (!s || !(s.rows || []).length) return;
    if (y + 60 > layout.PAGE_H - 44) { doc.addPage(); y = 44; }
    doc.setFontSize(10); doc.setFont(undefined, 'bold'); doc.setTextColor(...SLATE_900);
    doc.text(s.heading, layout.M, y);
    y = drawPdfTable(doc, layout, y + 6, s.columns, s.rows) + 22;
  });
  addFooters(doc, layout);
  doc.save(filename);
};

// Interactive, self-contained dashboard HTML — mirrors the in-app dashboard look.
// Exports in the app's current theme and embeds a dark/light toggle; everything
// (logo, data, styles, scripts) is inlined so the file works fully offline.
// opts: { kpis: [{label, value, accent}], score: {value, rating, legend:[{label,count,color}]} }
export const exportDashboardHtml = async (title, subtitle, sections, filename, opts = {}) => {
  let logoSrc = '';
  try {
    const res = await fetch(EXPORT_LOGO);
    if (res.ok) {
      const blob = await res.blob();
      logoSrc = await new Promise((resolve) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = () => resolve('');
        r.readAsDataURL(blob);
      });
    }
  } catch { /* logo optional */ }
  const esc = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const isDark = document.documentElement.classList.contains('dark');
  const ACCENTS = { blue: '#3b82f6', green: '#10b981', purple: '#8b5cf6', amber: '#f59e0b', red: '#ef4444', slate: '#64748b' };

  const kpiHtml = (opts.kpis || []).length ? `<div class="kpis">${opts.kpis.map((k) => `
<div class="kpi" style="background:${ACCENTS[k.accent] || ACCENTS.blue}"><div class="kpi-label">${esc(k.label)}</div><div class="kpi-value">${esc(k.value)}</div><span class="kpi-dot"></span></div>`).join('')}</div>` : '';

  let scoreHtml = '';
  const sc = opts.score;
  if (sc && sc.rating !== undefined) {
    const notRated = sc.rating === 'not_rated' || sc.value == null;
    const val = notRated ? null : Number(sc.value);
    const col = notRated ? '#64748b' : val >= 85 ? '#10b981' : val >= 60 ? '#f59e0b' : '#ef4444';
    const C = 2 * Math.PI * 42;
    const legend = sc.legend || [];
    const total = legend.reduce((a, l) => a + (l.count || 0), 0);
    let segs = '';
    if (!notRated && total > 0) {
      let off = 0;
      legend.forEach((l) => {
        if (!l.count) return;
        const len = (l.count / total) * C;
        segs += `<circle cx="50" cy="50" r="42" fill="none" stroke="${l.color}" stroke-width="10" stroke-dasharray="${Math.max(len - 2, 0.5).toFixed(1)} ${C.toFixed(1)}" stroke-dashoffset="${(-off).toFixed(1)}" transform="rotate(-90 50 50)"/>`;
        off += len;
      });
    } else if (!notRated) {
      segs = `<circle cx="50" cy="50" r="42" fill="none" stroke="${col}" stroke-width="10"/>`;
    }
    const tiles = total > 0
      ? `<div class="hb-tiles">${legend.map((l) => `<div class="hb-tile" style="background:${l.color}"><b>${esc(l.count)}</b><span>${esc(l.label)}</span></div>`).join('')}</div>`
      : `<div class="hb-msg" style="color:${notRated ? 'var(--muted)' : '#10b981'}">${notRated ? 'Not enough driving to rate yet' : 'No harsh driving events in this period.'}</div>`;
    const note = sc.note ? `<div class="hb-note">${esc(sc.note)}</div>` : '';
    scoreHtml = `<div class="card score-card"><div class="card-head"><h2>${esc(sc.heading || 'Driving Behaviour')}</h2></div><div class="score-body">
<svg viewBox="0 0 100 100" class="donut"><circle cx="50" cy="50" r="42" fill="none" stroke="var(--donut-track)" stroke-width="10"/>${segs}
<text x="50" y="48" text-anchor="middle" font-size="22" font-weight="700" fill="${col}">${notRated ? '—' : val}</text>
<text x="50" y="63" text-anchor="middle" font-size="7.5" fill="var(--muted)">${notRated ? 'Not rated' : sc.rating === 'low_confidence' ? 'Low confidence' : 'Score'}</text></svg>
<div class="hb-right">${tiles}${note}</div></div></div>`;
  }

  const chartsHtml = (opts.charts || []).filter((c) => (c.bars || []).length).map((c) => {    const W = 300; const H = 132; const plotH = 96; const top = 14;
    const max = Math.max(...c.bars.map((b) => Number(b.value) || 0), 1);
    const slot = W / c.bars.length;
    const bw = Math.min(slot * 0.55, 34);
    const rects = c.bars.map((b, i) => {
      const v = Number(b.value) || 0;
      const h = (v / max) * plotH;
      const x = i * slot + (slot - bw) / 2;
      const y = top + plotH - h;
      const cx = (i * slot + slot / 2).toFixed(1);
      return `<rect x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${bw.toFixed(1)}" height="${Math.max(h, 0).toFixed(1)}" rx="2.5" fill="${c.color}"/>
<text x="${cx}" y="${(y - 4).toFixed(1)}" text-anchor="middle" font-size="8" fill="var(--muted)">${v ? esc(v) : ''}</text>
<text x="${cx}" y="${H - 4}" text-anchor="middle" font-size="8.5" fill="var(--muted)">${esc(b.label)}</text>`;
    }).join('');
    return `<div class="card chart-card"><div class="card-head"><h2>${esc(c.heading)}</h2></div><svg class="chart" viewBox="0 0 ${W} ${H}"><line x1="0" y1="${top + plotH}" x2="${W}" y2="${top + plotH}" stroke="var(--border)" stroke-width="1"/>${rects}</svg></div>`;
  }).join('');
  let donutHtml = '';
  const dn = opts.donut;
  if (dn && (dn.slices || []).some((s) => Number(s.value) > 0)) {
    const slices = dn.slices.filter((s) => Number(s.value) > 0);
    const totalV = slices.reduce((a, s) => a + Number(s.value), 0);
    const C2 = 2 * Math.PI * 42;
    let off = 0; let arcs = '';
    slices.forEach((s) => {
      const len = (Number(s.value) / totalV) * C2;
      arcs += `<circle cx="50" cy="50" r="42" fill="none" stroke="${s.color}" stroke-width="15" stroke-dasharray="${Math.max(len - (slices.length > 1 ? 2 : 0), 0.5).toFixed(1)} ${C2.toFixed(1)}" stroke-dashoffset="${(-off).toFixed(1)}" transform="rotate(-90 50 50)"/>`;
      off += len;
    });
    const legRows = slices.map((s) => `<div class="dn-row"><span class="leg-dot" style="background:${s.color}"></span><span class="dn-name">${esc(s.label)}</span><b>${esc(s.value)}${dn.unit ? ` ${esc(dn.unit)}` : ''}</b></div>`).join('');
    donutHtml = `<div class="card chart-card"><div class="card-head"><h2>${esc(dn.heading)}</h2></div><div class="dn-body"><svg viewBox="0 0 100 100" class="dn-svg">${arcs}</svg><div class="dn-legend">${legRows}</div></div></div>`;
  }
  const chartsWrap = (chartsHtml || donutHtml) ? `<div class="charts">${chartsHtml}${donutHtml}</div>` : '';

  // "About the Driving Behaviour score" footnote — shown whenever the score section exists.
  const scoreNote = sc ? `<div class="card"><div class="card-head"><h2>About the Driving Behaviour score</h2></div>
<div class="snote">
<p>The score starts at <b>100</b> and deducts <b>5 points per harsh event per 100 km driven</b> in the window: <span class="snote-code">score = 100 &minus; 5 &times; (events &divide; km &times; 100)</span>. Distance-weighting means one event on a long-distance week barely moves the needle, while the same event on a short week matters a lot.</p>
<p><span class="sdot" style="background:#10b981"></span><b>85&ndash;100</b> Good &middot; <span class="sdot" style="background:#f59e0b"></span><b>60&ndash;84</b> Needs attention &middot; <span class="sdot" style="background:#ef4444"></span><b>0&ndash;59</b> Risky</p>
<p><b>Fair rating:</b> a score only shows with enough driving to be meaningful &mdash; under <b>50 km</b> in the window the card reads <i>Not rated</i>; <b>50&ndash;200 km</b> shows the score with low confidence; <b>200 km+</b> is fully rated. Thresholds are configurable in Settings &rsaquo; Driving Behaviour Rating.</p>
</div></div>` : '';

  let videoHtml = '';
  let videoBadge = '';
  const camSvg = '<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2"><path d="m22 8-6 4 6 4V8Z"/><rect x="2" y="6" width="14" height="12" rx="2"/></svg>';
  const carSvg = '<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12.2V16c0 .6.4 1 1 1h2"/><circle cx="7" cy="17" r="2"/><path d="M9 17h6"/><circle cx="17" cy="17" r="2"/></svg>';
  const playSvg = '<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>';
  const vid = opts.video;
  if (vid) {
    videoBadge = `<span class="vbadge">${camSvg} Video equipped${vid.cameras ? ` &middot; ${esc(vid.cameras)} camera${Number(vid.cameras) === 1 ? '' : 's'}` : ''}</span>`;
    const rows = (vid.clips || []).map((c) => `<div class="clip-row"><span class="clip-ic">${playSvg}</span><div class="clip-t"><b>${esc(c.label)}</b><span>${esc(c.channel)} &middot; ${esc(c.time)}</span></div></div>`).join('');
    videoHtml = `<div class="card"><div class="card-head"><h2>Video &mdash; Recent Event Clips</h2></div>
<div class="clip-rows">${rows || '<span class="clip-empty">No event videos yet</span>'}</div>
<div class="clip-note">Clips are stored securely &mdash; open EnsueLIVETRAK &rsaquo; Media &rsaquo; Event Videos to watch or download them.</div></div>`;
  }
  // Generic topbar badge (e.g. Person export "Driving <vehicle>") when no video badge.
  if (!videoBadge && opts.badge?.text) {
    const icon = opts.badge.icon === 'car' ? carSvg : camSvg;
    videoBadge = `<span class="vbadge" style="background:${opts.badge.color || '#2563eb'}">${icon} ${esc(opts.badge.text)}</span>`;
  }

  const secHtml = (sections || []).filter((s) => s && (s.rows || []).length).map((s) => `
<details class="card" open><summary class="card-head"><h2>${esc(s.heading)}</h2><span class="count">${s.rows.length}</span><svg class="chev" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><path d="m6 9 6 6 6-6"/></svg></summary>
<div class="tbl-wrap"><table><thead><tr>${s.columns.map((c) => `<th>${esc(c.label)}</th>`).join('')}</tr></thead>
<tbody>${s.rows.map((r) => `<tr>${r.map((c) => `<td>${esc(c)}</td>`).join('')}</tr>`).join('')}</tbody></table></div></details>`).join('');

  const ts = new Date().toLocaleString();
  const html = `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>${esc(title)}</title>
<style>
*{margin:0;padding:0;box-sizing:border-box}
:root{--bg:#f1f5f9;--card:#ffffff;--border:#e2e8f0;--text:#0f172a;--muted:#64748b;--head-bg:#f8fafc;--hover:#f1f5f9;--donut-track:#e2e8f0;--accent:#2563eb}
body.dark{--bg:#0b1220;--card:#131c2e;--border:#2a3650;--text:#e2e8f0;--muted:#94a3b8;--head-bg:#18233a;--hover:#1c2941;--donut-track:#2a3650}
body{font-family:system-ui,-apple-system,'Segoe UI',sans-serif;background:var(--bg);color:var(--text);transition:background .25s,color .25s}
.topbar{position:sticky;top:0;z-index:5;display:flex;align-items:center;gap:14px;padding:12px 20px;background:var(--card);border-bottom:1px solid var(--border)}
.topbar img{height:34px}
.ttl{flex:1;min-width:0}.ttl b{display:block;font-size:15px}.ttl span{font-size:12px;color:var(--muted)}
.btn{display:inline-flex;align-items:center;gap:6px;font-size:12px;font-weight:600;color:var(--text);background:var(--card);border:1px solid var(--border);border-radius:8px;padding:7px 12px;cursor:pointer;transition:background .15s}
.btn:hover{background:var(--hover)}
.wrap{max-width:1100px;margin:0 auto;padding:20px}
.search{width:100%;max-width:360px;margin-bottom:16px;padding:8px 12px;font-size:13px;color:var(--text);background:var(--card);border:1px solid var(--border);border-radius:9px;outline:none}
.search:focus{border-color:var(--accent)}
.kpis{display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:12px;margin-bottom:16px}
.kpi{position:relative;color:#fff;border-radius:12px;padding:14px 16px;overflow:hidden}
.kpi-label{font-size:10px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;color:rgba(255,255,255,.85)}
.kpi-value{font-size:20px;font-weight:700;margin-top:4px}
.kpi-dot{position:absolute;top:0;right:0;width:46px;height:46px;border-radius:0 12px 0 46px;background:#fff;opacity:.16}
.card{background:var(--card);border:1px solid var(--border);border-radius:12px;margin-bottom:14px;overflow:hidden}
.card-head{display:flex;align-items:center;gap:8px;padding:11px 16px;cursor:pointer;list-style:none;user-select:none}
.card-head::-webkit-details-marker{display:none}
.card-head h2{font-size:13px;font-weight:700;flex:1}
.count{font-size:10px;font-weight:600;color:var(--muted);background:var(--head-bg);border:1px solid var(--border);border-radius:99px;padding:1px 8px}
.chev{color:var(--muted);transition:transform .2s}
details[open] .chev{transform:rotate(180deg)}
.tbl-wrap{overflow-x:auto;border-top:1px solid var(--border)}
table{width:100%;border-collapse:collapse;font-size:12px}
th{background:var(--head-bg);color:var(--muted);font-weight:600;text-align:left;padding:7px 14px;white-space:nowrap;border-bottom:1px solid var(--border)}
td{padding:7px 14px;border-bottom:1px solid var(--border);vertical-align:top}
tbody tr:last-child td{border-bottom:none}
tbody tr{transition:background .12s}tbody tr:hover{background:var(--hover)}
.score-card{padding-bottom:4px}
.score-body{display:flex;align-items:center;gap:22px;padding:4px 18px 16px;flex-wrap:wrap}
.donut{width:130px;height:130px;flex-shrink:0}
.hb-right{flex:1;min-width:240px;display:flex;flex-direction:column;gap:10px}
.hb-tiles{display:grid;grid-template-columns:repeat(auto-fit,minmax(130px,1fr));gap:10px}
.hb-tile{color:#fff;border-radius:10px;padding:12px 10px;text-align:center}
.hb-tile b{display:block;font-size:20px;line-height:1.2}
.hb-tile span{font-size:11px;opacity:.92}
.hb-msg{font-size:13px;font-weight:600}
.hb-note{font-size:11px;color:var(--muted)}
.charts{display:grid;grid-template-columns:repeat(auto-fit,minmax(300px,1fr));gap:14px;margin-bottom:14px}
.chart-card{margin-bottom:0}
.chart{width:100%;display:block;box-sizing:border-box;padding:0 14px 8px}
.dn-body{display:flex;align-items:center;gap:18px;padding:4px 16px 12px}
.dn-svg{width:105px;height:105px;flex-shrink:0}
.dn-legend{flex:1;display:flex;flex-direction:column;gap:5px;font-size:11.5px}
.dn-row{display:flex;align-items:center;gap:7px}
.dn-row b{margin-left:auto}
.dn-name{color:var(--muted)}
.leg-dot{width:9px;height:9px;border-radius:99px;flex-shrink:0}
.mode-pill{display:inline-flex;align-items:center;gap:9px;font-size:12px;font-weight:600;color:var(--text);background:var(--head-bg);border:1px solid var(--border);border-radius:99px;padding:6px 6px 6px 13px;cursor:pointer;user-select:none}
.switch{position:relative;width:36px;height:20px;border-radius:99px;background:var(--border);transition:background .2s;flex-shrink:0}
.switch.on{background:#3b82f6}
.knob{position:absolute;top:2px;left:2px;width:16px;height:16px;border-radius:99px;background:#fff;transition:left .2s}
.switch.on .knob{left:18px}
.footer{padding:16px 0 24px;font-size:10px;color:var(--muted);text-align:center}
.vbadge{display:inline-flex;align-items:center;gap:6px;font-size:11px;font-weight:700;color:#fff;background:#dc2626;border-radius:99px;padding:5px 11px;white-space:nowrap}
.clip-rows{padding:4px 16px 12px;display:flex;flex-direction:column;gap:8px}
.clip-row{display:flex;align-items:center;gap:10px;border:1px solid var(--border);border-radius:10px;padding:8px 12px}
.clip-ic{width:30px;height:30px;border-radius:8px;background:var(--head-bg);display:flex;align-items:center;justify-content:center;color:var(--accent);flex-shrink:0}
.clip-t b{display:block;font-size:12px}
.clip-t span{font-size:11px;color:var(--muted)}
.clip-empty{font-size:12px;color:var(--muted)}
.clip-note{font-size:10.5px;color:var(--muted);padding:0 16px 12px}
.snote{padding:4px 16px 14px;font-size:11px;color:var(--muted);line-height:1.55}
.snote p{margin:0 0 6px}
.snote b{color:var(--fg)}
.snote-code{font-family:ui-monospace,Menlo,monospace;background:var(--head-bg);border-radius:6px;padding:1px 6px;font-size:10.5px}
.sdot{display:inline-block;width:8px;height:8px;border-radius:99px;margin:0 4px 0 2px;vertical-align:middle}
.snote{padding:4px 16px 14px;font-size:11px;color:var(--muted);line-height:1.55}
.snote p{margin:0 0 6px}
.snote b{color:var(--fg)}
.snote-code{font-family:ui-monospace,Menlo,monospace;background:var(--head-bg);border-radius:6px;padding:1px 6px;font-size:10.5px}
.sdot{display:inline-block;width:8px;height:8px;border-radius:99px;margin:0 4px 0 2px;vertical-align:middle}
@media print{.topbar{position:static}.btn,.search{display:none}body{background:#fff}.card{break-inside:avoid}@page{margin:10mm 8mm}}
</style></head><body class="${isDark ? 'dark' : ''}">
<div class="topbar">${logoSrc ? `<img src="${logoSrc}" alt="EnsueLIVETRAK"/>` : ''}<div class="ttl"><b>${esc(title)}</b><span>${esc(subtitle || '')}</span></div>
${videoBadge}<div class="mode-pill" id="modePill" title="Toggle dark / light mode"><span>Dark Mode</span><span class="switch${isDark ? ' on' : ''}" id="modeSw"><span class="knob"></span></span></div>
<button class="btn" onclick="window.print()">Print</button></div>
<div class="wrap">
<input class="search" id="q" type="search" placeholder="Search this dashboard..."/>
${kpiHtml}${scoreHtml}${chartsWrap}${videoHtml}${secHtml}${scoreNote}
<div class="footer">Generated by EnsueLIVETRAK &mdash; ${ts}</div></div>
<script>
document.getElementById('modePill').addEventListener('click',function(){
  var d=document.body.classList.toggle('dark');
  document.getElementById('modeSw').classList.toggle('on',d);
});
document.getElementById('q').addEventListener('input',function(){
  var q=this.value.toLowerCase();
  document.querySelectorAll('details.card').forEach(function(card){
    var vis=0;
    card.querySelectorAll('tbody tr').forEach(function(tr){
      var show=!q||tr.textContent.toLowerCase().indexOf(q)>-1;
      tr.style.display=show?'':'none';if(show)vis++;
    });
    var c=card.querySelector('.count');if(c)c.textContent=vis;
    card.style.display=(q&&vis===0)?'none':'';
    if(q&&vis>0)card.open=true;
  });
});
</script></body></html>`;
  const blob = new Blob([html], { type: 'text/html;charset=utf-8;' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
};

// Raw Packet Viewer HTML export — same branded, self-contained shell as the
// dashboard export (logo, dark toggle, search, print) with one expandable
// <details> card per packet. items: [{ index, time, serverTime, protocol,
// codec, event, speed, heading, position, altitude, satellites, bytes,
// rawHex, ios: [[key, label, value]] }]
export const exportRawPacketsHtml = async (title, subtitle, items, filename) => {
  let logoSrc = '';
  try {
    const res = await fetch(EXPORT_LOGO);
    if (res.ok) {
      const blob = await res.blob();
      logoSrc = await new Promise((resolve) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = () => resolve('');
        r.readAsDataURL(blob);
      });
    }
  } catch { /* logo optional */ }
  const esc = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const isDark = document.documentElement.classList.contains('dark');

  const pktHtml = (items || []).map((p) => `
<details class="card pkt"><summary class="card-head"><span class="idx">#${esc(p.index)}</span><span class="mono">${esc(p.serverTime)}</span><span class="badge">${esc(p.codec || p.protocol || '')}</span>${Number(p.speed) > 0 ? `<span class="spd">${esc(p.speed)} km/h</span>` : ''}${p.event ? `<span class="badge ev">${esc(p.event)}</span>` : ''}<span class="bytes">${p.bytes ? `${esc(p.bytes)}B` : ''}</span><svg class="chev" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><path d="m6 9 6 6 6-6"/></svg></summary>
<div class="pkt-body">
<div class="dec-grid">
<span>Timestamp</span><b class="mono">${esc(p.time)}</b>
<span>Server Time</span><b class="mono">${esc(p.serverTime)}</b>
<span>Protocol / Codec</span><b class="mono">${esc(p.protocol || '-')} / ${esc(p.codec || '-')}</b>
<span>Event</span><b class="mono">${esc(p.event || '-')}</b>
<span>Position</span><b class="mono">${esc(p.position || '-')}</b>
<span>Speed / Heading</span><b class="mono">${esc(p.speed ?? 0)} km/h / ${esc(p.heading ?? 0)}&deg;</b>
<span>Altitude / Satellites</span><b class="mono">${esc(p.altitude ?? 0)} m / ${esc(p.satellites ?? 0)}</b>
</div>
${(p.ios || []).length ? `<div class="tbl-wrap"><table><thead><tr><th>IO Key</th><th>Label</th><th>Value</th></tr></thead><tbody>${p.ios.map(([k, l, v]) => `<tr><td class="mono">${esc(k)}</td><td>${esc(l)}</td><td class="mono val">${esc(v)}</td></tr>`).join('')}</tbody></table></div>` : ''}
${p.rawHex ? `<div class="hex-head">Raw Packet (${esc(p.bytes)} bytes)</div><pre class="hex">${esc(p.rawHex)}</pre>` : ''}
</div></details>`).join('');

  const ts = new Date().toLocaleString();
  const html = `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>${esc(title)}</title>
<style>
*{margin:0;padding:0;box-sizing:border-box}
:root{--bg:#f1f5f9;--card:#ffffff;--border:#e2e8f0;--text:#0f172a;--muted:#64748b;--head-bg:#f8fafc;--hover:#f1f5f9;--accent:#2563eb}
body.dark{--bg:#0b1220;--card:#131c2e;--border:#2a3650;--text:#e2e8f0;--muted:#94a3b8;--head-bg:#18233a;--hover:#1c2941}
body{font-family:system-ui,-apple-system,'Segoe UI',sans-serif;background:var(--bg);color:var(--text);transition:background .25s,color .25s}
.topbar{position:sticky;top:0;z-index:5;display:flex;align-items:center;gap:14px;padding:12px 20px;background:var(--card);border-bottom:1px solid var(--border)}
.topbar img{height:34px}
.ttl{flex:1;min-width:0}.ttl b{display:block;font-size:15px}.ttl span{font-size:12px;color:var(--muted)}
.btn{display:inline-flex;align-items:center;gap:6px;font-size:12px;font-weight:600;color:var(--text);background:var(--card);border:1px solid var(--border);border-radius:8px;padding:7px 12px;cursor:pointer;transition:background .15s}
.btn:hover{background:var(--hover)}
.mode-pill{display:inline-flex;align-items:center;gap:9px;font-size:12px;font-weight:600;color:var(--text);background:var(--head-bg);border:1px solid var(--border);border-radius:99px;padding:6px 6px 6px 13px;cursor:pointer;user-select:none}
.switch{position:relative;width:36px;height:20px;border-radius:99px;background:var(--border);transition:background .2s;flex-shrink:0}
.switch.on{background:#3b82f6}
.knob{position:absolute;top:2px;left:2px;width:16px;height:16px;border-radius:99px;background:#fff;transition:left .2s}
.switch.on .knob{left:18px}
.wrap{max-width:1100px;margin:0 auto;padding:20px}
.search{width:100%;max-width:360px;margin-bottom:16px;padding:8px 12px;font-size:13px;color:var(--text);background:var(--card);border:1px solid var(--border);border-radius:9px;outline:none}
.search:focus{border-color:var(--accent)}
.card{background:var(--card);border:1px solid var(--border);border-radius:12px;margin-bottom:10px;overflow:hidden}
.card-head{display:flex;align-items:center;gap:10px;padding:10px 14px;cursor:pointer;list-style:none;user-select:none;font-size:12px}
.card-head::-webkit-details-marker{display:none}
.card-head:hover{background:var(--hover)}
.idx{color:var(--muted);font-family:ui-monospace,Menlo,monospace;flex-shrink:0}
.mono{font-family:ui-monospace,Menlo,monospace}
.badge{font-size:10px;font-weight:600;border:1px solid var(--border);border-radius:99px;padding:1px 8px;white-space:nowrap}
.badge.ev{color:#fff;background:#d97706;border-color:#d97706}
.spd{color:var(--muted)}
.bytes{margin-left:auto;color:var(--muted);font-size:11px;flex-shrink:0}
.chev{color:var(--muted);transition:transform .2s;flex-shrink:0}
details[open] .chev{transform:rotate(180deg)}
.pkt-body{border-top:1px solid var(--border);padding:12px 14px}
.dec-grid{display:grid;grid-template-columns:150px 1fr;gap:3px 14px;font-size:12px;margin-bottom:10px}
.dec-grid span{color:var(--muted)}
.dec-grid b{font-weight:500;word-break:break-all}
.tbl-wrap{overflow-x:auto;border:1px solid var(--border);border-radius:8px;margin-bottom:10px}
table{width:100%;border-collapse:collapse;font-size:11.5px}
th{background:var(--head-bg);color:var(--muted);font-weight:600;text-align:left;padding:6px 12px;white-space:nowrap;border-bottom:1px solid var(--border)}
td{padding:5px 12px;border-bottom:1px solid var(--border);vertical-align:top}
td.val{word-break:break-all}
tbody tr:last-child td{border-bottom:none}
tbody tr:hover{background:var(--hover)}
.hex-head{font-size:11px;font-weight:600;color:var(--muted);margin-bottom:6px}
.hex{background:#020617;color:#4ade80;padding:12px;border-radius:8px;font-size:11px;font-family:ui-monospace,Menlo,monospace;line-height:1.6;white-space:pre-wrap;word-break:break-all;user-select:all}
.footer{padding:16px 0 24px;font-size:10px;color:var(--muted);text-align:center}
@media print{.topbar{position:static}.btn,.search,.mode-pill{display:none}body{background:#fff}.card{break-inside:avoid}@page{margin:10mm 8mm}}
</style></head><body class="${isDark ? 'dark' : ''}">
<div class="topbar">${logoSrc ? `<img src="${logoSrc}" alt="EnsueLIVETRAK"/>` : ''}<div class="ttl"><b>${esc(title)}</b><span>${esc(subtitle || '')}</span></div>
<button class="btn" id="expAll">Expand all</button>
<div class="mode-pill" id="modePill" title="Toggle dark / light mode"><span>Dark Mode</span><span class="switch${isDark ? ' on' : ''}" id="modeSw"><span class="knob"></span></span></div>
<button class="btn" onclick="window.print()">Print</button></div>
<div class="wrap">
<input class="search" id="q" type="search" placeholder="Search packets (event, IO, value, hex)..."/>
${pktHtml}
<div class="footer">Generated by EnsueLIVETRAK &mdash; ${ts} &mdash; ${(items || []).length} packet${(items || []).length === 1 ? '' : 's'}</div></div>
<script>
document.getElementById('modePill').addEventListener('click',function(){
  var d=document.body.classList.toggle('dark');
  document.getElementById('modeSw').classList.toggle('on',d);
});
var expAll=document.getElementById('expAll'),open=false;
expAll.addEventListener('click',function(){
  open=!open;
  document.querySelectorAll('details.pkt').forEach(function(d){d.open=open;});
  expAll.textContent=open?'Collapse all':'Expand all';
});
document.getElementById('q').addEventListener('input',function(){
  var q=this.value.toLowerCase();
  document.querySelectorAll('details.pkt').forEach(function(d){
    var show=!q||d.textContent.toLowerCase().indexOf(q)>-1;
    d.style.display=show?'':'none';
    if(q&&show)d.open=true;
  });
});
</script></body></html>`;
  const blob = new Blob([html], { type: 'text/html;charset=utf-8;' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
};

export const exportTripCsv = (trip, vehicleName, idx = 0) => {
  const rows = [['Vehicle', vehicleName || ''], []].concat(tripCsvRows(trip, idx));
  downloadBlob(rowsToCsv(rows), `${safeName(vehicleName)}-trip-${idx + 1}-${stamp()}.csv`, 'text/csv;charset=utf-8;');
};

export const exportAllTripsCsv = (trips, vehicleName) => {
  let rows = [['Vehicle', vehicleName || ''], ['Trips', String((trips || []).length)], ['Exported', formatExportDateTime(new Date().toISOString())], []];
  (trips || []).forEach((trip, idx) => {
    rows = rows.concat(tripCsvRows(trip, idx), [[], []]);
  });
  downloadBlob(rowsToCsv(rows), `${safeName(vehicleName)}-driver-logbook_${stamp()}.csv`, 'text/csv;charset=utf-8;');
};

// ----------------------------- PDF -----------------------------

export const newLandscapeDoc = () => {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const M = 40;
  const PAGE_W = doc.internal.pageSize.getWidth();   // ~842
  const PAGE_H = doc.internal.pageSize.getHeight();  // ~595
  return { doc, layout: { M, PAGE_W, PAGE_H, RIGHT: PAGE_W - M, CONTENT_W: PAGE_W - 2 * M } };
};

export const addBrandHeader = (doc, layout, logo, title, subtitle) => {
  const { M, RIGHT } = layout;
  let y = 36;
  if (logo) {
    const h = 28;
    const w = (logo.w / logo.h) * h;
    try { doc.addImage(logo.dataUrl, 'PNG', M, y, w, h); } catch { /* skip */ }
  } else {
    doc.setFontSize(15); doc.setFont(undefined, 'bold'); doc.setTextColor(...SLATE_900);
    doc.text('EnsueLIVETRAK', M, y + 19);
  }
  doc.setFontSize(14); doc.setFont(undefined, 'bold'); doc.setTextColor(...SLATE_900);
  doc.text(title, RIGHT, y + 12, { align: 'right' });
  doc.setFontSize(9); doc.setFont(undefined, 'normal'); doc.setTextColor(...SLATE_600);
  doc.text(subtitle || '-', RIGHT, y + 26, { align: 'right' });
  y += 40;
  doc.setDrawColor(...BLUE); doc.setLineWidth(1.5); doc.line(M, y, RIGHT, y);
  return y + 18;
};

export const addFooters = (doc, layout) => {
  const { M, RIGHT, PAGE_W, PAGE_H } = layout;
  const generatedAt = formatExportDateTime(new Date().toISOString());
  const pages = doc.getNumberOfPages();
  for (let p = 1; p <= pages; p += 1) {
    doc.setPage(p);
    doc.setDrawColor(...BORDER); doc.setLineWidth(0.5); doc.line(M, PAGE_H - 30, RIGHT, PAGE_H - 30);
    doc.setFontSize(8); doc.setFont(undefined, 'normal'); doc.setTextColor(...SLATE_500);
    doc.text(`Generated by EnsueLIVETRAK - ${generatedAt}`, PAGE_W / 2, PAGE_H - 18, { align: 'center' });
    doc.text(`Page ${p} / ${pages}`, RIGHT, PAGE_H - 18, { align: 'right' });
  }
};

// Render one trip block starting at `startY`; returns the new y.
const drawTripBlock = (doc, layout, trip, idx, startY) => {
  const { M, CONTENT_W, PAGE_H } = layout;
  let y = startY;

  doc.setFontSize(11); doc.setFont(undefined, 'bold'); doc.setTextColor(...SLATE_900);
  const titleEnd = trip.is_active ? 'Ongoing' : formatExportDateTime(trip.ended_at);
  doc.text(`${formatExportDateTime(trip.started_at)}  ->  ${titleEnd}`, M, y);
  doc.setFontSize(9); doc.setFont(undefined, 'bold');
  doc.setTextColor(...(trip.is_active ? GREEN : SLATE_500));
  const durDist = [
    trip.duration_seconds != null ? formatDuration(trip.duration_seconds) : null,
    trip.distance_km != null ? formatDistance(trip.distance_km, { decimals: 1 }) : null,
  ].filter(Boolean).join('  |  ');
  if (durDist) doc.text(durDist, layout.RIGHT, y, { align: 'right' });
  y += 18;

  // Start / End GPS locations (clickable -> Google Maps)
  const startUrl = tripLocMapsUrl(trip.start_location);
  const endUrl = tripLocMapsUrl(trip.end_location);
  if (startUrl || endUrl) {
    doc.setFontSize(8); doc.setFont(undefined, 'normal'); doc.setTextColor(...BLUE);
    let lx = M;
    if (startUrl) {
      const s = `Start: ${fmtCoords(trip.start_location)}`;
      doc.textWithLink(s, lx, y, { url: startUrl });
      lx += doc.getTextWidth(s) + 24;
    }
    if (endUrl) {
      const e = `End: ${fmtCoords(trip.end_location)}`;
      doc.textWithLink(e, lx, y, { url: endUrl });
    }
    y += 16;
  }
  y += 4;

  const ballotRows = ballotAuditRows(trip);
  ballotRows.forEach(([label, value]) => {
    const lines = doc.splitTextToSize(`${label}: ${value}`, CONTENT_W - 8);
    if (y + (lines.length * 11) > PAGE_H - 44) { doc.addPage(); y = 44; }
    doc.setFontSize(8); doc.setFont(undefined, 'normal'); doc.setTextColor(...SLATE_600);
    doc.text(lines, M + 4, y + 9);
    y += Math.max(13, lines.length * 11);
  });
  if (ballotRows.length) y += 4;

  // Occupants table (wide landscape columns -> no truncation)
  const cols = [
    { label: 'Role', w: 60 },
    { label: 'Name', w: 150 },
    { label: 'Source', w: 95 },
    { label: 'Credential', w: 160 },
    { label: 'Boarded -> Left', w: CONTENT_W - 465 },
  ];
  const xs = [];
  let cx = M;
  cols.forEach((c) => { xs.push(cx); cx += c.w; });
  const ROW_H = 16;

  const drawTableHeader = () => {
    doc.setFillColor(...HEAD_BG);
    doc.rect(M, y, CONTENT_W, ROW_H, 'F');
    doc.setFontSize(8); doc.setFont(undefined, 'bold'); doc.setTextColor(...SLATE_600);
    cols.forEach((c, i) => doc.text(c.label, xs[i] + 4, y + 11));
    y += ROW_H;
  };

  drawTableHeader();
  const occ = trip.occupants || [];
  doc.setFont(undefined, 'normal'); doc.setFontSize(8);
  if (occ.length === 0) {
    doc.setTextColor(...SLATE_500);
    doc.text(trip.no_driver ? 'No driver identified.' : 'No occupants recorded for this trip.', M + 4, y + 11); y += ROW_H;
  }
  occ.forEach((o, i) => {
    if (y + ROW_H > PAGE_H - 44) { doc.addPage(); y = 44; drawTableHeader(); doc.setFont(undefined, 'normal'); doc.setFontSize(8); }
    if (i % 2 === 1) { doc.setFillColor(...ROW_BG); doc.rect(M, y, CONTENT_W, ROW_H, 'F'); }
    doc.setTextColor(...SLATE_900);
    const cells = [
      roleLabel(o.role || o.person_type),
      String(o.person_name || o.resolved_person_name || 'Unmatched'),
      String(o.source_label || ''),
      String(o.credential_identifier || '-'),
      `${formatExportDateTime(o.started_at)} -> ${o.is_active ? 'onboard' : formatExportDateTime(o.ended_at)}`,
    ];
    cells.forEach((val, ci) => {
      const fit = doc.splitTextToSize(val, cols[ci].w - 8)[0] || '';
      doc.text(fit, xs[ci] + 4, y + 11);
    });
    y += ROW_H;
    const transition = roleTransitionLabel(o.role_transition);
    const detailRows = [
      ...(transition ? [['Role history', transition]] : []),
      ...(o.allocation_note ? [['Allocation / decision', o.allocation_note]] : []),
      ...assignmentAuditRows(o.assignment_audit),
    ];
    detailRows.forEach(([label, value]) => {
      const lines = doc.splitTextToSize(`${label}: ${value}`, CONTENT_W - 8);
      if (y + (lines.length * 11) > PAGE_H - 44) { doc.addPage(); y = 44; }
      doc.setTextColor(...BLUE);
      doc.text(lines, M + 4, y + 10);
      y += Math.max(ROW_H, lines.length * 11);
    });
  });
  y += 10;

  if ((trip.rejected_scans || []).length) {
    if (y + 40 > PAGE_H - 44) { doc.addPage(); y = 44; }
    doc.setFont(undefined, 'bold'); doc.setFontSize(10); doc.setTextColor(...AMBER);
    doc.text(`Rejected scans (${trip.rejected_scans.length})`, M, y); y += 14;
    doc.setFont(undefined, 'normal'); doc.setFontSize(8); doc.setTextColor(...SLATE_900);
    trip.rejected_scans.forEach((r) => {
      if (y > PAGE_H - 44) { doc.addPage(); y = 44; }
      doc.text(`- ${r.person_name || 'Unknown'} - ${r.credential_identifier || '-'} (${formatExportDateTime(r.timestamp)})`, M, y);
      y += 13;
    });
  }
  return y;
};

export const exportTripPdf = async (trip, vehicleName, idx = 0) => {
  const { doc, layout } = newLandscapeDoc();
  const logo = await loadLogoPng();
  let y = addBrandHeader(doc, layout, logo, 'Vehicle Driver Trip Report', vehicleName);
  y = drawTripBlock(doc, layout, trip, idx, y);
  addFooters(doc, layout);
  doc.save(`${safeName(vehicleName)}-trip-${idx + 1}-${stamp()}.pdf`);
};

export const buildAllTripsPdf = async (trips, vehicleName) => {
  const { doc, layout } = newLandscapeDoc();
  const logo = await loadLogoPng();
  let y = addBrandHeader(doc, layout, logo, 'Vehicle Driver Log Book', `${vehicleName || '-'} \u00b7 ${(trips || []).length} trips`);
  const list = trips || [];
  list.forEach((trip, i) => {
    if (y > layout.PAGE_H - 140) { doc.addPage(); y = 44; }
    y = drawTripBlock(doc, layout, trip, i, y);
    y += 16;
    if (i < list.length - 1) {
      doc.setDrawColor(...BORDER); doc.setLineWidth(0.5); doc.line(layout.M, y - 8, layout.RIGHT, y - 8);
    }
  });
  if (list.length === 0) {
    doc.setFontSize(10); doc.setTextColor(...SLATE_500);
    doc.text('No trips found for the selected filters.', layout.M, y);
  }
  addFooters(doc, layout);
  return { doc, filename: `${safeName(vehicleName)}-driver-logbook_${stamp()}.pdf` };
};

export const exportAllTripsPdf = async (trips, vehicleName) => {
  const { doc, filename } = await buildAllTripsPdf(trips, vehicleName);
  doc.save(filename);
};

// Return the built PDF as a base64 string (no data: prefix) for emailing.
export const pdfDocToBase64 = (doc) => String(doc.output('datauristring')).split(',')[1] || '';
